import { useMemo, useState } from "react";
import {
  Badge,
  DescriptionList,
  EmptyState,
  ErrorState,
  FormSectionLabel,
  LoadingState,
  Modal,
  Table,
  Tabs,
  Text,
  type Column,
} from "@marekl/adminer/components";
import { formatDate } from "@marekl/adminer/format";
import {
  usePersonDetail,
  usePersonMemberships,
  type PersonLeadershipRow,
  type PersonListItem,
  type PersonMembershipRow,
} from "../../lib/queries/persons";
import { ROLE_LABEL } from "../../lib/i18n/roles";
import { MemberPhoto } from "../MemberPhoto";
import { memberBadge } from "../../lib/memberState";
import { useSeason } from "../../season";
import { usePhotoVersion } from "../../lib/photoVersion";
import { RegisteredTick } from "../RegisteredTick";
import type { ID } from "../../lib/id";
import styles from "./PersonDetail.module.css";

type TabId = "udaje" | "clenstvi";

const dash = <Text variant="muted">—</Text>;

function orDash(value: string | null | undefined) {
  return value && value.trim() !== "" ? value : dash;
}

function address(p: PersonListItem): string {
  const city = [p.postal_code, p.city].filter(Boolean).join(" ");
  return [p.street, city].filter(Boolean).join(", ");
}

function Details({ person }: { person: PersonListItem }) {
  // Membership is a per-season fact, so the row names the season it was
  // computed against (is_member is the ACTIVE season's answer). Outside a
  // table it is a badge, as in the registry's edit dialog.
  const { activeSeason } = useSeason();
  const badge = memberBadge(person.is_member ? "registered" : "guest");
  const teams = person.teams_label
    ? person.last_season
      ? `${person.teams_label} (${person.last_season})`
      : person.teams_label
    : person.last_season;
  return (
    <DescriptionList>
      {activeSeason && (
        <DescriptionList.Item label={`Sezóna ${activeSeason.code}`}>
          <Badge variant={badge.variant}>{badge.label}</Badge>
        </DescriptionList.Item>
      )}
      <DescriptionList.Item label="Datum narození">
        {person.birth_date ? formatDate(person.birth_date) : dash}
      </DescriptionList.Item>
      <DescriptionList.Item label="Rodné číslo">{orDash(person.birth_number)}</DescriptionList.Item>
      <DescriptionList.Item label="Státní příslušnost">{orDash(person.country_code)}</DescriptionList.Item>
      <DescriptionList.Item label="Adresa">{orDash(address(person))}</DescriptionList.Item>
      <DescriptionList.Item label="Telefon">{orDash(person.phone)}</DescriptionList.Item>
      <DescriptionList.Item label="ČNSMS ID">
        {person.cmf_id ? <Text variant="mono">{person.cmf_id}</Text> : dash}
      </DescriptionList.Item>
      <DescriptionList.Item label="Přihlašovací e-mail">{orDash(person.login_email)}</DescriptionList.Item>
      <DescriptionList.Item label="Týmy (poslední sezóna)">{orDash(teams)}</DescriptionList.Item>
    </DescriptionList>
  );
}

function rolesLabel(m: PersonMembershipRow): string {
  return m.roles.map((r) => ROLE_LABEL[r]).join(", ");
}

function Memberships({ personId }: { personId: ID }) {
  const history = usePersonMemberships(personId);

  // A modal table: never sortable (AGENTS.md §5). The rows come newest
  // season first, which is the order a history is read in.
  const membershipColumns = useMemo<Column<PersonMembershipRow>[]>(
    () => [
      {
        key: "season",
        header: "Sezóna",
        render: (m) => (m.season_active ? <strong>{m.season_code}</strong> : m.season_code),
      },
      { key: "team", header: "Tým", render: (m) => m.team_name },
      {
        key: "registered",
        header: "Registrovaný",
        render: (m) => <RegisteredTick registered={m.kind === "registered"} />,
      },
      { key: "roles", header: "Role", render: (m) => orDash(rolesLabel(m)) },
      {
        key: "roster",
        header: "Na soupisce",
        // One column, so the table fits the dialog: from the date put on the
        // roster, to the date taken off it. A season ending closes nothing,
        // so an end date means a removal during the season.
        render: (m) =>
          m.valid_to
            ? `${formatDate(m.valid_from)}–${formatDate(m.valid_to)}`
            : `od ${formatDate(m.valid_from)}`,
      },
    ],
    [],
  );
  const leadershipColumns = useMemo<Column<PersonLeadershipRow>[]>(
    () => [
      { key: "team", header: "Tým", render: (l) => l.team_name },
      { key: "from", header: "Od", render: (l) => formatDate(l.valid_from) },
      { key: "to", header: "Do", render: (l) => (l.valid_to ? formatDate(l.valid_to) : "dosud") },
    ],
    [],
  );

  const leaderships = history.data?.leaderships ?? [];
  return (
    <div className={styles.stack}>
      <Table
        columns={membershipColumns}
        rows={history.data?.memberships ?? []}
        rowKey={(m) => m.id}
        isLoading={history.isLoading}
        error={history.error}
        onRetry={() => history.refetch()}
        empty={
          <EmptyState title="Žádné členství" description="Osoba není na soupisce žádného týmu v žádné sezóně." />
        }
      />
      {leaderships.length > 0 && (
        <div className={styles.stack}>
          <FormSectionLabel>Vedoucí týmu</FormSectionLabel>
          <Table columns={leadershipColumns} rows={leaderships} rowKey={(l) => `${l.team_id}:${l.valid_from}`} />
        </div>
      )}
    </div>
  );
}

/**
 * PersonDetailModal shows one person: their details, and in tabs what else
 * the app knows about them (today „Členství", every membership across all
 * seasons). Read-only, so a plain Modal and not a FormModal; editing stays
 * in EditPersonModal. Opened through PersonDetailHost by the `osoba` search
 * parameter, never mounted by a page.
 */
export function PersonDetailModal({ personId, onClose }: { personId: ID; onClose: () => void }) {
  const [tab, setTab] = useState<TabId>("udaje");
  const person = usePersonDetail(personId);
  const p = person.data;
  // Bumped by any photo upload in this tab (lib/photoVersion), so a photo
  // replaced through the registry or the roster shows here at once.
  const photoVersion = usePhotoVersion(personId);

  let body;
  if (person.isLoading) {
    body = <LoadingState label="Načítám osobu…" />;
  } else if (person.error || !p) {
    body = <ErrorState error={person.error} onRetry={() => person.refetch()} />;
  } else {
    body = (
      <div className={styles.stack}>
        {/* The roster's round chip, large and centred: the same face the
            lists show, so the dialog reads as that row opened up. MemberPhoto
            also falls back to the initials when the stored photo fails. */}
        <div className={styles.head}>
          <MemberPhoto
            personId={p.id}
            firstName={p.first_name}
            lastName={p.last_name}
            hasPhoto={p.photo_uploaded}
            version={photoVersion}
            size={128}
          />
          <Text as="p" variant="muted">
            {p.cmf_id ? `ČNSMS ID ${p.cmf_id}` : "Bez ČNSMS ID"}
          </Text>
        </div>
        <Tabs
          aria-label="Osoba"
          tabs={[
            { id: "udaje", label: "Údaje" },
            { id: "clenstvi", label: "Členství" },
          ]}
          value={tab}
          onChange={(id) => setTab(id as TabId)}
        />
        {tab === "udaje" ? <Details person={p} /> : <Memberships personId={p.id} />}
      </div>
    );
  }

  return (
    <Modal open title={p ? `${p.first_name} ${p.last_name}` : "Osoba"} onClose={onClose} width={600}>
      {body}
    </Modal>
  );
}
